import * as React from "react";
import { Contents } from "../../../../../Shared/Components/Resource/Contents";
import { DatePicker } from "../../../../../../react-core/Datepicker/DatePicker";
import { SelectEditorFactory } from "../../../../../../react-core/Html/Factories/SelectEditorFactory";
import { BaseComponent } from "../../../../../../react-core/Html/BaseComponent";
import { JsonDateParser } from "../../../../../../react-core/Utils/JsonDateParser";
import { DashboardDateRangeOption, DashboardDateRangeOptions } from "../../Resource/Content";
import { DateHelper } from "../../../../../../react-core/Helpers/DateHelper";
import { addDays } from "../../../../../../react-core/utils/date";

interface IDateRangeProps {
    minDate?: Date;
    optionKey: DashboardDateRangeOption;
    beginDate?: Date;
    showDateRangeText?: boolean;
    endDate?: Date;
    disabled?: boolean;
    onChange: (beginDate: Date, endDate: Date, optionKey: string, isFromAndTo?: boolean) => void;
}

interface IDateRangeState {
    optionKey?: string;
    fromDateSelected?: Date;
    toDateSelected?: Date;
}

export class DashboardDateRange extends BaseComponent<IDateRangeProps, IDateRangeState> {
    constructor(props: IDateRangeProps) {
        super(props);
        const initDataRangeOption = this.onDateSelectChange(props.optionKey || "Past 6 months");

        this.state = {
            optionKey: props.optionKey ? props.optionKey : "Past 6 months",
            fromDateSelected: props.beginDate ? props.beginDate : initDataRangeOption.fromDateSelected,
            toDateSelected: props.endDate ? props.endDate : initDataRangeOption.toDateSelected,
        };
    }

    private isLeapDay(endDate: Date) {
        const year = endDate.getFullYear();
        const isLeapYear = year % 100 === 0 ? year % 400 === 0 : year % 4 === 0;
        const leapdaystr = endDate.getFullYear() + "/02/29";
        const todaystr = JsonDateParser.getFormatDateString(endDate, "yyyy/MM/dd");
        return isLeapYear && todaystr === leapdaystr;
    }

    private getPastYear(endDate: Date): Date {
        return this.isLeapDay(endDate)
            ? new Date(endDate.getFullYear() - 1, 2, 1)
            : new Date(endDate.getFullYear() - 1, endDate.getMonth(), endDate.getDate() + 1);
    }

    public componentDidMount() {
        this.props.onChange(this.state.fromDateSelected, this.state.toDateSelected, this.state.optionKey);
    }

    public componentWillReceiveProps(nextProps: IDateRangeProps) {
        if (nextProps.optionKey != this.props.optionKey) {
            this.setState({ optionKey: nextProps.optionKey });
        }

        if (nextProps.beginDate != this.props.beginDate) {
            this.setState({ fromDateSelected: nextProps.beginDate });
        }
        if (nextProps.endDate != this.props.endDate) {
            this.setState({ toDateSelected: nextProps.endDate });
        }
    }

    private onDateSelectChange(key: string) {
        let beginDate: Date = null;
        let endDate: Date = null;
        let fromYear = null;
        let toYear = null;
        const now = new Date();
        now.setHours(0, 0, 0);
        const nowStr = JsonDateParser.getFormatDateString(now, "yyyy/MM/dd").split("/");
        const pointDate = new Date(Number(nowStr[0]), 3, 6);

        switch (key) {
            case "Past tax year":
                if (now > pointDate) {
                    fromYear = Number(nowStr[0]) - 1;
                    toYear = Number(nowStr[0]);
                    beginDate = new Date(fromYear, 3, 6);
                    endDate = new Date(toYear, 3, 5);
                } else {
                    fromYear = Number(nowStr[0]) - 2;
                    toYear = Number(nowStr[0]) - 1;
                    beginDate = new Date(fromYear, 3, 6);
                    endDate = new Date(toYear, 3, 5);
                }
                break;
            case "Current tax year": //"Current tax year"
                if (now > pointDate) {
                    fromYear = Number(nowStr[0]);
                    beginDate = new Date(fromYear, 3, 6);
                    endDate = now;
                } else {
                    fromYear = Number(nowStr[0]) - 1;
                    beginDate = new Date(fromYear, 3, 6);
                    endDate = now;
                }
                break;
            case "Custom": //"Custom"
                beginDate = this.getPastYear(now);
                endDate = now;
                break;
            case "Past year": //"Past year"
                beginDate = this.getPastYear(now);
                endDate = now;
                break;
            case "Past 6 months": //"Past 6 months"
                beginDate = this.isLeapDay(now)
                    ? new Date(now.getFullYear() - 1, 8, 1)
                    : new Date(now.getFullYear(), now.getMonth() - 6, now.getDate() + 1);
                endDate = now;
                break;
            case "Past 3 months":
                beginDate = this.isLeapDay(now)
                    ? new Date(now.getFullYear() - 1, 12, 1)
                    : new Date(now.getFullYear(), now.getMonth() - 3, now.getDate() + 1);
                endDate = now;
                break;
            case "Past month":
                beginDate = this.isLeapDay(now)
                    ? new Date(now.getFullYear(), 2, 1)
                    : new Date(now.getFullYear(), now.getMonth() - 1, now.getDate() + 1);
                endDate = now;
                break;
        }
        return { optionKey: key, fromDateSelected: beginDate, toDateSelected: endDate };
    }

    private renderFromDate(): JSX.Element {
        const { fromDateSelected, toDateSelected, optionKey } = this.state;

        if (optionKey !== "Custom") {
            return null;
        }
        const _selected = fromDateSelected ? fromDateSelected : new Date();

        const minFromDate = toDateSelected ? addDays(toDateSelected, -365) : null;

        return (
            <div className="col-12">
                <DatePicker
                    required={Contents.CanNotEmpty}
                    label={Contents.From}
                    labelSRSufix="dd/mm/yyyy"
                    selected={_selected}
                    atMost={{ value: toDateSelected, errorMessage: Contents.DateCanNotInFuture }}
                    atLeast={{ value: minFromDate, errorMessage: Contents.DateNoMoreThanOneYearApart }}
                    showYearDropdown={true}
                    alwaysValidateOnUpdate
                    onChange={(d) =>
                        this.setState({ fromDateSelected: d }, () => {
                            this.props.onChange(d, toDateSelected, optionKey, true);
                        })
                    }
                    todayButton="Today"
                    isClearable={false}
                    dateFormat={"dd/MM/yyyy"}
                    acceptDateFormats={[
                        "dd/MM/yyyy",
                        "dd-MM-yyyy",
                        "ddMMyyyy",
                        "dd.MM.yyyy",
                        "dd_MM_yyyy",
                        "dd MM yyyy",
                    ]}
                    acceptDateFormatsErrorMsg={Contents.AcceptDateFormatsErrorMsg}
                />
            </div>
        );
    }

    private renderToDate(): JSX.Element {
        const { fromDateSelected, toDateSelected, optionKey } = this.state;
        const now = new Date();
        if (optionKey !== "Custom") {
            return null;
        }

        return (
            <div className="col-12">
                <DatePicker
                    required={Contents.CanNotEmpty}
                    label={Contents.To}
                    alwaysValidateOnUpdate
                    labelSRSufix="dd/mm/yyyy"
                    selected={toDateSelected}
                    atLeast={{ value: fromDateSelected, errorMessage: Contents.DateCanNotInPast }}
                    atMost={{ value: now, errorMessage: Contents.DateCanNotInFuture }}
                    showYearDropdown={true}
                    onChange={(d) =>
                        this.setState({ toDateSelected: d }, () => {
                            this.props.onChange(fromDateSelected, d, optionKey, true);
                        })
                    }
                    todayButton="Today"
                    isClearable={false}
                    dateFormat={"dd/MM/yyyy"}
                    acceptDateFormats={["dd/MM/yyyy", "dd-MM-yyyy", "ddMMyyyy"]}
                    acceptDateFormatsErrorMsg={Contents.AcceptDateFormatsErrorMsg}
                />
            </div>
        );
    }

    private renderDateRangeStaticText(): string {
        const { fromDateSelected, toDateSelected } = this.state;
        return `From ${DateHelper.getDateStringAsddmmmyyyy(
            fromDateSelected,
            true
        )} until ${DateHelper.getDateStringAsddmmmyyyy(toDateSelected, true)}`;
    }

    public render(): JSX.Element {
        const { optionKey } = this.state;

        const DateRange = SelectEditorFactory.create<string>();
        return (
            <>
                <div className="row">
                    <div className="col-12">
                        <DateRange
                            disabled={this.props.disabled}
                            cssWrapper="control-size-430"
                            key="Date"
                            label="Date"
                            selected={optionKey}
                            options={DashboardDateRangeOptions}
                            getText={(x) => x}
                            onChange={(val) => {
                                const { fromDateSelected, toDateSelected, optionKey } = this.onDateSelectChange(val);
                                this.setState({ fromDateSelected, toDateSelected, optionKey }, () => {
                                    this.props.onChange(fromDateSelected, toDateSelected, optionKey);
                                });
                            }}
                        />
                    </div>
                    {this.props.showDateRangeText && (
                        <div className="col-12 col-middle col-sm-fluid col-max-245">
                            <p className="text-size-12 mt-27">
                                From {DateHelper.getDateStringAsddmmmyyyy(this.state.fromDateSelected)} until{" "}
                                {DateHelper.getDateStringAsddmmmyyyy(this.state.toDateSelected)}
                            </p>
                        </div>
                    )}
                </div>
                <div className="row">
                    {this.renderFromDate()}
                    {this.renderToDate()}
                </div>
                <div className="row">
                    <div className="col-12">
                        <p className="text-size-14">{this.renderDateRangeStaticText()}</p>
                    </div>
                </div>
            </>
        );
    }
}
